import React, { useState, useEffect } from 'react';
import './ProtocolBuilder.css';
import { Icon } from './Icon.js';
import ProtocolScheduler from './ProtocolScheduler';
import ProtocolShield from './ProtocolShield';

const ProtocolBuilder = ({ selectedHacks, onRemoveHack, onSaveProtocol, onNavigateToMyProtocols, onAddHack, onError, shouldClearInputs, onShowDropSuccess, onShowDropRemove, onClearAll, isSaving = false, currentUsername = '' }) => {
  const [protocolName, setProtocolName] = useState('');
  const [protocolDescription, setProtocolDescription] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const [isHoveringBiohacks, setIsHoveringBiohacks] = useState(false);
  const [showScheduler, setShowScheduler] = useState(false);
  const [scheduleOption, setScheduleOption] = useState(null);
  const [scheduleData, setScheduleData] = useState({});
  const [confirmedSchedules, setConfirmedSchedules] = useState(new Set());
  const [isExpanded, setIsExpanded] = useState(false); // Mobile state

  // Clear inputs and reset scheduler when needed
  useEffect(() => {
    if (shouldClearInputs) {
      setProtocolName('');
      setProtocolDescription('');
      // Close scheduler and reset schedule state
      setShowScheduler(false);
      setScheduleOption(null);
      setScheduleData({});
      setConfirmedSchedules(new Set());
      setIsExpanded(false); // Collapse on clear
    }
  }, [shouldClearInputs]);

  // Auto-expand when a hack is added on mobile (optional UX improvement)
  useEffect(() => {
    if (selectedHacks.length > 0 && window.innerWidth <= 800) {
      // setIsExpanded(true); // Uncomment if you want auto-expand
    }
  }, [selectedHacks.length]);

  const handleSave = () => {
    if (!protocolName.trim()) {
      onError('Please enter a protocol name');
      return;
    }

    if (selectedHacks.length === 0) {
      onError('Please select at least one biohack');
      return;
    }

    const protocolData = {
      name: protocolName.trim(),
      description: protocolDescription.trim(),
      status: 'active',
      scheduleOption: scheduleOption,
      scheduleData: scheduleData,
      confirmedSchedules: Array.from(confirmedSchedules) // Convert Set to Array for serialization
    };

    onSaveProtocol(protocolData);
  };

  const handleScheduleChange = (option, data, confirmed) => {
    setScheduleOption(option);
    setScheduleData(data);
    setConfirmedSchedules(confirmed || new Set());
  };

  const handleCloseScheduler = () => {
    setShowScheduler(false);
  };

  // Check if at least one schedule is confirmed (user pressed Done or Apply to all)
  const hasAtLeastOneCompleteSchedule = () => {
    return confirmedSchedules.size > 0;
  };

  // Determine if Create Protocol button should be enabled
  const isCreateProtocolEnabled = () => {
    // Basic requirements
    if (selectedHacks.length === 0 || !protocolName.trim()) {
      return false;
    }
    
    // If no schedule option selected, button is disabled
    if (!scheduleOption) {
      return false;
    }
    
    // If "no schedule" is selected, button is enabled
    if (scheduleOption === 'none') {
      return true;
    }
    
    // If "set schedule" is selected, button is only enabled if at least one schedule is complete
    if (scheduleOption === 'normal') {
      return hasAtLeastOneCompleteSchedule();
    }
    
    return false;
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    // Only remove drag over state if leaving the entire drop zone
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    try {
      const biohackData = JSON.parse(e.dataTransfer.getData('application/json'));
      
      // Check if biohack is already selected
      if (selectedHacks.some(hack => hack.id === biohackData.id)) {
        if (onShowDropRemove) {
          onShowDropRemove();
        }
        return;
      }
      
      // Check if we've reached the maximum (8 biohacks)
      if (selectedHacks.length >= 8) {
        onError('Maximum 8 biohacks allowed per protocol');
        return;
      }
      
      // Add the biohack
      onAddHack(biohackData);
      if (onShowDropSuccess) {
        onShowDropSuccess();
      }
    } catch (error) {
      console.error('Error handling drop:', error);
      onError('Error adding biohack');
    }
  };

  // Create protocol preview object for the card
  const protocolPreview = protocolName.trim() ? {
    id: 'preview',
    name: protocolName.trim(),
    description: protocolDescription.trim(),
    biohacks: selectedHacks,
    created_by_username: currentUsername || undefined
  } : null;

  return (
    <>
      {/* Backdrop when scheduler is open */}
      {showScheduler && (
        <>
          <div
            className="protocol-builder-backdrop"
            onClick={handleCloseScheduler}
          />
          {/* Protocol Card Preview in Center */}
          {protocolPreview && (
            <div className="protocol-preview-overlay" onClick={handleCloseScheduler}>
              <div className="protocol-preview-wrapper" onClick={e => e.stopPropagation()}>
                <p className="protocol-preview-label">Preview protocol</p>
                <div className="protocol-preview-container">
                <ProtocolShield
                  protocol={protocolPreview}
                  size="medium"
                  disableFlip={true}
                  hideFabMenu={true}
                  hideInspectButton={true}
                />
                </div>
              </div>
            </div>
          )}
        </>
      )}

      <div className={`protocol-builder ${isExpanded ? 'expanded' : 'collapsed'}`}>
        {/* Mobile Header / Handle */}
        <div 
          className="protocol-mobile-handle"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="mobile-handle-content">
            <span className="mobile-handle-title">
              Protocol Builder
              {selectedHacks.length > 0 && (
                <span className="mobile-badge">{selectedHacks.length}</span>
              )}
            </span>
            <Icon 
              name={isExpanded ? "ChevronDown" : "ChevronUp"} 
              className="icon--sm" 
            />
          </div>
        </div>

        {!showScheduler ? (
          <>
            <div className="protocol-header">
              <h3>Create Protocol</h3>
              <div className="protocol-input-container">
                <input
                  type="text"
                  value={protocolName}
                  onChange={(e) => setProtocolName(e.target.value)}
                  placeholder="Protocol name..."
                  className="protocol-name-input"
                />
                <div className="input-divider"></div>
                <textarea
                  value={protocolDescription}
                  onChange={(e) => setProtocolDescription(e.target.value)}
                  placeholder="Description (optional)..."
                  className="protocol-description-input"
                  rows={2}
                />
              </div>
            </div>

            <div className="protocol-content">
              <div 
                className="biohacks-section"
                onMouseEnter={() => setIsHoveringBiohacks(true)}
                onMouseLeave={() => setIsHoveringBiohacks(false)}
              >
                <div className="biohacks-divider"></div>
                <div className="biohacks-header">
                  <h4>
                    <Icon name="Zap" className="icon--sm" />
                    Biohacks
                  </h4>
                  {selectedHacks.length > 0 && isHoveringBiohacks && (
                    <button 
                      className="clear-all-btn"
                      onClick={() => onClearAll(selectedHacks.length)}
                      title="Clear all biohacks"
                    >
                      <Icon name="Trash2" className="icon--xs" />
                      Clear All
                    </button>
                  )}
                </div>
                <div 
                  className={`biohacks-list ${isDragOver ? 'drag-over' : ''}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  {selectedHacks.map((hack, index) => (
                    <div key={hack.id} className="biohack-item-wrapper">
                      <div className="biohack-number">{index + 1}</div>
                      <div className="biohack-item">
                        <img 
                          src={hack.image_url || '/default-biohack.jpg'} 
                          alt={hack.name}
                          className="biohack-image"
                        />
                        <span className="biohack-name">{hack.name}</span>
                        <button 
                          className="remove-biohack"
                          onClick={() => onRemoveHack(hack.id)}
                          title="Remove biohack"
                        >
                          <Icon name="X" className="icon--xs" />
                        </button>
                      </div>
                    </div>
                  ))}
                  
                  {selectedHacks.length === 0 && (
                    <>
                      <div className="empty-state-wrapper">
                        <div className="biohack-number">1</div>
                        <div className="empty-state-simple">
                          <Icon name="Plus" className="icon--md" />
                          <span>Add biohacks</span>
                        </div>
                      </div>
                      <p className="empty-state-hint">Drag biohacks into bar</p>
                    </>
                  )}
                  
                  {selectedHacks.length > 0 && selectedHacks.length < 8 && (
                    <div className="empty-slot-wrapper">
                      <div className="biohack-number">{selectedHacks.length + 1}</div>
                      <div className="empty-slot-item">
                        <Icon name="Plus" className="icon--md" />
                        <span>Add another biohack</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        ) : (
          <ProtocolScheduler
            selectedHacks={selectedHacks}
            onScheduleChange={handleScheduleChange}
            onClose={handleCloseScheduler}
          />
        )}

        <div className="protocol-actions">
          {!showScheduler ? (
            <button 
              className="btn btn-primary"
              onClick={() => setShowScheduler(true)}
              disabled={selectedHacks.length === 0 || !protocolName.trim()}
            >
              <Icon name="Clock" className="icon--sm" />
              <span className="btn-text-desktop">Schedule Protocol</span>
              <span className="btn-text-mobile">Schedule</span>
            </button>
          ) : (
            <button 
              className={`btn btn-primary ${!isCreateProtocolEnabled() || isSaving ? 'btn-primary--disabled' : ''}`}
              onClick={handleSave}
              disabled={!isCreateProtocolEnabled() || isSaving}
            >
              <Icon name="Save" className="icon--sm" />
              {isSaving ? (
                'Creating...'
              ) : (
                <>
                  <span className="btn-text-desktop">Create Protocol</span>
                  <span className="btn-text-mobile">Create</span>
                </>
              )}
            </button>
          )}
          
          <button 
            className="btn btn-secondary"
            onClick={onNavigateToMyProtocols}
          >
            My Protocols
            <Icon name="ExternalLink" className="icon--sm" />
          </button>
        </div>
      </div>
    </>
  );
};

export default ProtocolBuilder;